import { useState, useCallback } from 'react';
import api from './api';

const STORAGE_KEY = 'userProfile';

const AUDIENCE_LABELS = {
  importer: 'Importer / distributor',
  government: 'Government procurement',
  defence: 'Defence / defence industry',
};

// Map legacy industry_sector values to audience types
const SECTOR_TO_AUDIENCE = {
  manufacturing: 'importer',
  logistics: 'importer',
  other: 'importer',
  agribusiness: 'importer',
  mining: 'importer',
  government: 'government',
  defence: 'defence',
};

const ROLE_PRIORITY_SIGNALS = {
  importer: ['freight_logistics', 'trade_policy', 'currency_financial', 'climate_disaster', 'supplier_concentration'],
  government: ['geopolitical', 'regulatory', 'trade_policy', 'supplier_concentration', 'currency_financial'],
  defence: ['geopolitical', 'regulatory', 'trade_policy', 'supplier_concentration'],
  // Legacy role mappings
  'supply-chain': ['freight_logistics', 'supplier_concentration', 'trade_policy'],
  procurement: ['supplier_concentration', 'freight_logistics', 'trade_policy'],
  risk: ['regulatory', 'geopolitical', 'currency_financial'],
  executive: ['geopolitical', 'trade_policy', 'currency_financial'],
  operations: ['freight_logistics', 'trade_policy', 'regulatory'],
  other: [],
};

export function useUserProfile() {
  // SECURITY: sessionStorage holds non-sensitive personalisation preferences only (no auth tokens).
  // Auth tokens live in httpOnly cookies; preferences are mirrored server-side via /user-profile/me.
  const [profile, setProfileState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const setProfile = useCallback((data) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setProfileState(data);
    // Persist server-side so preferences survive new sessions/devices (fire-and-forget)
    api.saveMyPreferences(data).catch(() => {});
  }, []);

  const clearProfile = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('userProfileId');
    // Clean up any legacy localStorage remnants
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('userProfileId');
    setProfileState(null);
  }, []);

  const hasProfile = profile && (profile.company_name || profile.audience_type);

  // Determine audience type — new field or inferred from legacy industry_sector
  const audienceType = profile?.audience_type || SECTOR_TO_AUDIENCE[profile?.industry_sector] || null;

  const audienceLabel = AUDIENCE_LABELS[audienceType] || null;

  const isEnterprise = ['100m-500m', '500m-plus', '25m-100m', 'over-100m'].includes(profile?.company_size || profile?.annual_import_value);

  const rolePrioritySignals = ROLE_PRIORITY_SIGNALS[audienceType] || ROLE_PRIORITY_SIGNALS[profile?.primary_role] || [];

  const monitoredCountries = profile?.monitored_countries || [];

  const riskCategories = profile?.risk_categories || [];

  const tradeFootprint = profile?.trade_footprint || {};
  const homeCurrency = profile?.home_currency || 'USD';
  const homeCountry = profile?.home_country || 'AU';
  const selectedPorts = profile?.selected_ports || [];

  // Audience-specific data
  const importCategories = profile?.import_categories || [];
  const annualImportValue = profile?.annual_import_value || '';
  const govLevel = profile?.gov_level || '';
  const govGoods = profile?.gov_goods || [];
  const hasApacPanels = profile?.has_apac_panels || '';
  const defenceRole = profile?.defence_role || '';
  const capabilityDomains = profile?.capability_domains || [];
  const aukusSubject = profile?.aukus_subject || '';

  return {
    profile,
    setProfile,
    clearProfile,
    hasProfile,
    audienceType,
    audienceLabel,
    isEnterprise,
    rolePrioritySignals,
    monitoredCountries,
    riskCategories,
    tradeFootprint,
    homeCurrency,
    homeCountry,
    selectedPorts,
    importCategories,
    annualImportValue,
    govLevel,
    govGoods,
    hasApacPanels,
    defenceRole,
    capabilityDomains,
    aukusSubject,
    // Legacy compat
    sectorDisplayName: audienceLabel,
  };
}

export { AUDIENCE_LABELS, ROLE_PRIORITY_SIGNALS };
